import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ViewUrlQrCode, ViewUrlQrCodeHandle } from "../../common/ViewUrlQrCode";
import { useI18n } from "../../../../shared/i18n/i18n";
import { formatFullDateForUiLanguage } from "../../../../shared/i18n/dateFormatting";
import { TranslationKey } from "../../../../shared/i18n/i18n";
import { MANAGED_DOMAIN_TARGET_HOSTNAME, VIEW_BASE_URL } from "../../../../shared/config/runtimeConfig";
import { FaExternalLinkAlt, FaQrcode, FaRegCopy } from "react-icons/fa";
import {
  archiveAdminSign,
  createAdminSign,
  getAdminSite,
  getAdminSign,
  unarchiveAdminSign,
  updateAdminSign,
} from "../../../../shared/requests";
import {
  AdminSignArchiveRequest,
  AdminAllowedViewDomain,
  AdminOrganizationPermissions,
  AdminSignCreateRequest,
  AdminSignDetail,
  AdminSignDraft,
  AdminSignGetRequest,
  AdminSignUpdateRequest,
  AdminSiteGetRequest,
} from "../../../types/adminEntities";
import { useAdminOrganizationContextCache } from "../AdminOrganizationContextCache";
import { useAdminLoading } from "../AdminLoadingContext";
import { AdminNavSelection } from "../AdminNavList";
import { useAdminOrganizationContext } from "../useAdminOrganizationContext";
import { AppPopup } from "../../../../shared/components/AppPopup";
import { AdminActionButton } from "../../common/AdminActionButton";
import { AdminDetailActions } from "./AdminDetailActions";
import { AdminDetailSectionLayout } from "./AdminDetailSectionLayout";
import { AdminTextField } from "./AdminTextField";
import { DisabledActionReason, hasOrganizationPermission } from "../../../utils/adminPermissions";

type SignDetailSectionProps = {
  currentUserSuperAdmin: boolean;
  organizationId: null | string;
  isActive: boolean;
  isDesktopNavHidden: boolean;
  isMobileViewport: boolean;
  onCloseDesktopNav: () => void;
  onEntityMutated: () => void;
  onOpenDesktopNav: () => void;
  onSelectionChange: (selection: AdminNavSelection | null) => void;
  signTab: SignDetailTab;
  signId: null | string;
  siteId: null | string;
  t: (key: TranslationKey) => string;
};

type SignDetailMode = "create" | "edit" | "view";
type SignDetailTab = "details" | "urls";

const DEFAULT_SIGN_DRAFT: AdminSignDraft = {
  description: "",
  name: "",
};

const sanitizeQrFileNamePart = (value: string) => value
  .normalize("NFKD")
  .replace(/[\u0300-\u036f]/gu, "")
  .replace(/[^a-zA-Z0-9._-]+/gu, "_")
  .replace(/_+/gu, "_")
  .replace(/^[._-]+|[._-]+$/gu, "");

const buildQrFileName = (detail: AdminSignDetail) => {
  const parts = [
    sanitizeQrFileNamePart(detail.siteName ?? ""),
    sanitizeQrFileNamePart(detail.name ?? ""),
  ].filter((part) => part.length > 0);
  return `${parts.join("-")}.svg`;
};

const copyToClipboard = async (value: string) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "absolute";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
};

type AllowedViewUrlOption = Omit<AdminAllowedViewDomain, "domainType"> & {
  domainType: AdminAllowedViewDomain["domainType"];
  url: string;
};

export const SignDetailSection = ({
  currentUserSuperAdmin,
  organizationId,
  isActive,
  isDesktopNavHidden,
  isMobileViewport,
  onCloseDesktopNav,
  onEntityMutated,
  onOpenDesktopNav,
  onSelectionChange,
  signTab,
  signId,
  siteId,
  t,
}: SignDetailSectionProps) => {
  const { language } = useI18n();
  const { track } = useAdminLoading();
  const { ensureOrganizationContext } = useAdminOrganizationContextCache();
  const { organizationContext } = useAdminOrganizationContext(organizationId);
  const [organizationPermissions, setOrganizationPermissions] = useState<AdminOrganizationPermissions | null>(null);
  const [detail, setDetail] = useState<AdminSignDetail | null>(null);
  const [draft, setDraft] = useState<AdminSignDraft>(DEFAULT_SIGN_DRAFT);
  const [errorMessage, setErrorMessage] = useState<null | string>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [mode, setMode] = useState<SignDetailMode>("view");
  const [popup, setPopup] = useState<"archive" | "discard" | "unarchive" | null>(null);
  const [activeTab, setActiveTab] = useState<SignDetailTab>("details");
  const [qrPopupDomain, setQrPopupDomain] = useState<AllowedViewUrlOption | null>(null);
  const qrCodeRef = useRef<ViewUrlQrCodeHandle | null>(null);

  const loadDetail = useCallback(async (nextOrganizationId: string, nextSiteId: string, nextSignId: string) => {
    setErrorMessage(null);
    setIsBusy(true);
    setMode("view");
    try {
      const request: AdminSignGetRequest = { organizationId: nextOrganizationId, signId: nextSignId, siteId: nextSiteId };
      const siteRequest: AdminSiteGetRequest = { organizationId: nextOrganizationId, siteId: nextSiteId };
      const [{ payload: signPayload }, { payload: sitePayload }] = await Promise.all([
        track(async () => getAdminSign(request)),
        track(async () => getAdminSite(siteRequest)),
      ]);
      setDetail({ ...signPayload, siteName: sitePayload.name });
      setDraft({ description: signPayload.description ?? "", name: signPayload.name ?? "" });
    } catch (error) {
      const fallbackMessage = t("admin.detail.error.loadFailed");
      const message = error instanceof Error ? error.message : fallbackMessage;
      setDetail(null);
      setErrorMessage(message || fallbackMessage);
    } finally {
      setIsBusy(false);
    }
  }, [t, track]);

  useEffect(() => {
    if (!isActive) {
      return;
    }
    if (!organizationId || !siteId || !signId) {
      setDetail(null);
      setOrganizationPermissions(organizationContext?.organizationPermissions ?? null);
      setDraft(DEFAULT_SIGN_DRAFT);
      setErrorMessage(null);
      setIsBusy(false);
      setMode("create");
      setPopup(null);
      setActiveTab("details");
      setQrPopupDomain(null);
      return;
    }
    void loadDetail(organizationId, siteId, signId);
  }, [organizationContext?.organizationPermissions, organizationId, isActive, loadDetail, signId, siteId]);

  useEffect(() => {
    if (mode === "create") {
      setActiveTab("details");
    }
  }, [mode]);

  useEffect(() => {
    if (mode !== "create" && activeTab !== signTab) {
      setActiveTab(signTab);
    }
  }, [activeTab, mode, signTab]);

  useEffect(() => {
    setOrganizationPermissions(organizationContext?.organizationPermissions ?? null);
  }, [organizationContext]);

  const signPermissionMissing = Boolean(organizationContext) && !hasOrganizationPermission(organizationPermissions, "SIGN_CREATE");
  const signQuotaReached = Boolean(organizationContext) && organizationContext.organizationUsage.activeSignCount >= organizationContext.activeSubscription.maxActiveSigns;
  const createDisabledReason: DisabledActionReason = signPermissionMissing
    ? "permission"
    : signQuotaReached
      ? "subscription-limit"
      : null;
  const unarchiveDisabledReason: DisabledActionReason = signPermissionMissing
    ? "permission"
    : signQuotaReached
      ? "subscription-limit"
      : null;
  const canCreate = organizationId !== null && siteId !== null && hasOrganizationPermission(organizationPermissions, "SIGN_CREATE") && !signQuotaReached;
  const canUpdate = hasOrganizationPermission(organizationPermissions, "SIGN_UPDATE");
  const canArchive = hasOrganizationPermission(organizationPermissions, "SIGN_ARCHIVE");
  const canUnarchive = canArchive && !signQuotaReached;
  const updateDisabledReason: DisabledActionReason = !hasOrganizationPermission(organizationPermissions, "SIGN_UPDATE") ? "permission" : null;
  const archiveDisabledReason: DisabledActionReason = !hasOrganizationPermission(organizationPermissions, "SIGN_ARCHIVE") ? "permission" : null;

  const handleCreate = () => {
    if (!organizationId || !siteId) {
      return;
    }
    onSelectionChange({ organizationId, create: true, siteId, type: "sign" });
  };

  const handleEdit = () => {
    if (!detail || !canUpdate) {
      return;
    }
    setDraft({ description: detail.description, name: detail.name });
    setMode("edit");
  };

  const confirmDiscard = () => {
    if (!signId) {
      onSelectionChange(null);
      return;
    }
    setMode("view");
    if (organizationId && siteId) {
      void loadDetail(organizationId, siteId, signId);
    }
  };

  const handleSave = async () => {
    if (!organizationId || !siteId || (mode === "create" && !canCreate) || (mode === "edit" && !canUpdate)) {
      return;
    }
    setErrorMessage(null);
    setIsBusy(true);
    try {
      if (mode === "create") {
        const request: AdminSignCreateRequest = {
          organizationId,
          description: draft.description,
          name: draft.name,
          siteId,
        };
        const { payload } = await track(async () => createAdminSign(request));
        await ensureOrganizationContext(organizationId, { force: true });
        const createdId = payload.id;
        onEntityMutated();
        if (createdId) {
          onSelectionChange({ organizationId, signId: createdId, siteId, type: "sign" });
        }
      } else if (mode === "edit" && detail) {
        const request: AdminSignUpdateRequest = {
          organizationId,
          description: draft.description,
          name: draft.name,
          signId: detail.id,
          siteId: detail.siteId,
        };
        await track(async () => updateAdminSign(request));
        await ensureOrganizationContext(organizationId, { force: true });
        setDetail({ ...detail, description: draft.description, name: draft.name });
        setMode("view");
        onEntityMutated();
      }
    } catch (error) {
      const fallbackMessage = t("admin.detail.error.saveFailed");
      const message = error instanceof Error ? error.message : fallbackMessage;
      setErrorMessage(message || fallbackMessage);
    } finally {
      setIsBusy(false);
    }
  };

  const confirmArchive = async () => {
    if (!detail || !organizationId || !canArchive) {
      return;
    }
    setErrorMessage(null);
    setIsBusy(true);
    try {
      const request: AdminSignArchiveRequest = { organizationId, signId: detail.id, siteId: detail.siteId };
      await track(async () => archiveAdminSign(request));
      await ensureOrganizationContext(organizationId, { force: true });
      if (organizationId && siteId && signId) {
        await loadDetail(organizationId, siteId, signId);
      }
      onEntityMutated();
    } catch (error) {
      const fallbackMessage = t("admin.detail.error.archiveFailed");
      const message = error instanceof Error ? error.message : fallbackMessage;
      setErrorMessage(message || fallbackMessage);
    } finally {
      setIsBusy(false);
    }
  };

  const confirmUnarchive = async () => {
    if (!detail || !organizationId || !canUnarchive) {
      return;
    }
    setErrorMessage(null);
    setIsBusy(true);
    try {
      const request: AdminSignArchiveRequest = { organizationId, signId: detail.id, siteId: detail.siteId };
      await track(async () => unarchiveAdminSign(request));
      await ensureOrganizationContext(organizationId, { force: true });
      if (organizationId && siteId && signId) {
        await loadDetail(organizationId, siteId, signId);
      }
      onEntityMutated();
    } catch (error) {
      const fallbackMessage = t("admin.detail.error.unarchiveFailed");
      const message = error instanceof Error ? error.message : fallbackMessage;
      setErrorMessage(message || fallbackMessage);
    } finally {
      setIsBusy(false);
    }
  };

  const handlePopupConfirm = async () => {
    const nextPopup = popup;
    setPopup(null);
    if (nextPopup === "discard") {
      confirmDiscard();
    } else if (nextPopup === "archive") {
      await confirmArchive();
    } else if (nextPopup === "unarchive") {
      await confirmUnarchive();
    }
  };

  const popupTitle = popup === "discard"
    ? t("admin.detail.confirm.discardTitle")
    : popup === "archive"
      ? t("admin.detail.confirm.archiveTitle")
      : popup === "unarchive"
        ? t("admin.detail.confirm.unarchiveTitle")
        : "";

  const popupBody = popup === "discard"
    ? t("admin.detail.confirm.discard")
    : popup === "archive"
      ? t("admin.detail.confirm.archive")
      : popup === "unarchive"
        ? t("admin.detail.confirm.unarchive")
        : "";

  const isReadOnly = mode === "view" || isBusy || (mode === "create" && !canCreate) || (mode === "edit" && !canUpdate);
  const shouldRenderPane = mode === "create" || detail !== null || errorMessage !== null;
  const viewBaseUrl = useMemo(() => {
    try {
      return new URL(VIEW_BASE_URL);
    } catch {
      return null;
    }
  }, []);
  const viewPathPrefix = viewBaseUrl?.pathname.replace(/\/$/, "") ?? "/v";
  const viewProtocol = viewBaseUrl?.protocol ?? "https:";
  const viewPort = viewBaseUrl?.port ? `:${viewBaseUrl.port}` : "";
  const allowedViewDomains = detail?.allowedViewDomains ?? [];
  const allowedViewUrls = useMemo<AllowedViewUrlOption[]>(() => {
    if (!detail?.uuid) {
      return [];
    }

    const domains = allowedViewDomains.map((domain) => ({
      ...domain,
      url: `${viewProtocol}//${domain.hostname}${viewPort}${viewPathPrefix}/${detail.uuid}`,
    }));

    const edgeHostname = MANAGED_DOMAIN_TARGET_HOSTNAME.trim();
    if (!currentUserSuperAdmin || !edgeHostname || domains.some((domain) => domain.hostname === edgeHostname)) {
      return domains;
    }

    return [
      ...domains,
      {
        domainType: "EDGE",
        hostname: edgeHostname,
        selectable: true,
        url: `${viewProtocol}//${edgeHostname}${viewPort}${viewPathPrefix}/${detail.uuid}`,
      },
    ];
  }, [allowedViewDomains, currentUserSuperAdmin, detail?.uuid, viewPathPrefix, viewPort, viewProtocol]);
  const buildQrFileNameForDomain = useCallback((domain: AllowedViewUrlOption) => {
    return detail
      ? buildQrFileName(detail).replace(/\.svg$/, `-${domain.hostname}.svg`)
      : "qr-code.svg";
  }, [detail]);

  if (!shouldRenderPane) {
    return null;
  }

  return (
    <>
      <AdminDetailSectionLayout
        actions={detail ? (
          <AdminDetailActions
            archiveDisabledReason={archiveDisabledReason}
            canArchive={canArchive}
            canUnarchive={canUnarchive}
            canCreate={canCreate}
            createActionLabel={t("admin.detail.action.createSign")}
            canUpdate={canUpdate}
            createDisabledReason={createDisabledReason}
            hasDetail={detail !== null}
            disableNonEditingActions={mode === "create" || mode === "edit"}
            isArchived={Boolean(detail?.archivedAt)}
            isBusy={isBusy}
            isDesktopNavHidden={isDesktopNavHidden}
            isEditing={false}
            isMobileViewport={isMobileViewport}
            onArchive={() => setPopup("archive")}
            onCloseDesktopNav={onCloseDesktopNav}
            onCreate={handleCreate}
            onDiscard={() => setPopup("discard")}
            onEdit={handleEdit}
            onOpenDesktopNav={onOpenDesktopNav}
            onSave={() => void handleSave()}
            showEditAction={false}
            onUnarchive={() => setPopup("unarchive")}
            updateDisabledReason={updateDisabledReason}
            t={t}
          />
        ) : null}
        errorMessage={errorMessage}
        eyebrow={t("admin.detail.eyebrow")}
        title={t("admin.detail.title.sign")}
      >
        <div
          aria-label={t("admin.sign.tabs.title")}
          className="admin-profile-tabs"
          role="tablist"
        >
          <button
            aria-controls="admin-sign-details-panel"
            aria-selected={activeTab === "details"}
            className={`admin-profile-tab${activeTab === "details" ? " is-active" : ""}`}
            id="admin-sign-details-tab"
            onClick={() => {
              if (organizationId && siteId && detail) {
                onSelectionChange({ organizationId, signId: detail.id, signTab: "details", siteId, type: "sign" });
                return;
              }
              setActiveTab("details");
            }}
            role="tab"
            type="button"
          >
            {t("admin.sign.tab.details")}
          </button>
          <button
            aria-controls="admin-sign-urls-panel"
            aria-selected={activeTab === "urls"}
            className={`admin-profile-tab${activeTab === "urls" ? " is-active" : ""}`}
            disabled={mode === "create"}
            id="admin-sign-urls-tab"
            onClick={() => {
              if (organizationId && siteId && detail) {
                onSelectionChange({ organizationId, signId: detail.id, signTab: "urls", siteId, type: "sign" });
                return;
              }
              setActiveTab("urls");
            }}
            role="tab"
            type="button"
          >
            {t("admin.sign.tab.urls")}
          </button>
        </div>
        <section
          aria-labelledby="admin-sign-details-tab"
          className={`admin-profile-panel${activeTab === "details" ? " is-active" : ""}`}
          id="admin-sign-details-panel"
          role="tabpanel"
        >
          <div className="admin-profile-section-header">
            <div className="admin-detail-actions admin-profile-section-actions">
              <AdminDetailActions
                canCreate={canCreate}
                canUpdate={mode === "create" ? canCreate : canUpdate}
                createDisabledReason={createDisabledReason}
                hasDetail={detail !== null}
                isArchived={Boolean(detail?.archivedAt)}
                isBusy={isBusy}
                isDesktopNavHidden={isDesktopNavHidden}
                isEditing={mode === "create" || mode === "edit"}
                isMobileViewport={isMobileViewport}
                onArchive={() => {}}
                onCloseDesktopNav={onCloseDesktopNav}
                onCreate={handleCreate}
                onDiscard={() => setPopup("discard")}
                onEdit={handleEdit}
                onOpenDesktopNav={onOpenDesktopNav}
                onSave={() => void handleSave()}
                onUnarchive={() => {}}
                showArchiveActions={false}
                showCreateAction={false}
                showNavToggle={false}
                updateDisabledReason={mode === "create" ? createDisabledReason : updateDisabledReason}
                t={t}
              />
            </div>
          </div>
          {detail || mode === "create" ? (
            <form className="admin-detail-form">
              {detail?.uuid ? (
                <AdminTextField
                  isReadOnly
                  label={t("admin.detail.field.uuid")}
                  t={t}
                  value={detail.uuid}
                  visibility="public"
                />
              ) : null}
              <AdminTextField
                isReadOnly={isReadOnly}
                label={t("admin.detail.field.name")}
                onChange={isReadOnly ? undefined : (value) => setDraft((current) => ({ ...current, name: value }))}
                t={t}
                value={draft.name}
                visibility="internal"
              />
              <AdminTextField
                isReadOnly={isReadOnly}
                label={t("admin.detail.field.description")}
                onChange={isReadOnly ? undefined : (value) => setDraft((current) => ({ ...current, description: value }))}
                t={t}
                value={draft.description}
                visibility="internal"
              />
              {detail?.archivedAt ? (
                <AdminTextField
                  isReadOnly
                  label={t("admin.detail.field.archivedAt")}
                  t={t}
                  value={formatFullDateForUiLanguage(detail.archivedAt, language)}
                  visibility="internal"
                />
              ) : null}
            </form>
          ) : null}
        </section>
        <section
          aria-labelledby="admin-sign-urls-tab"
          className={`admin-profile-panel${activeTab === "urls" ? " is-active" : ""}`}
          id="admin-sign-urls-panel"
          role="tabpanel"
        >
          {allowedViewUrls.length > 0 ? (
            <div className="admin-profile-terms-table-wrap admin-organization-users-table-wrap">
              <table className="admin-profile-terms-table admin-organization-users-table">
                <thead>
                  <tr>
                    <th scope="col">{t("admin.organization.domains.status")}</th>
                    <th scope="col">{t("admin.organization.domains.managed.hostname")}</th>
                    <th scope="col">{t("admin.profile.terms.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {allowedViewUrls.map((domain) => (
                    <tr key={domain.hostname}>
                      <td data-label={t("admin.organization.domains.status")}>
                        {t(`admin.organization.domains.type.${domain.domainType}` as TranslationKey)}
                        {domain.selectable ? "" : ` · ${t("admin.organization.domains.connected.notVerifiedYet")}`}
                      </td>
                      <td data-label={t("admin.organization.domains.managed.hostname")}>
                        {domain.hostname}
                      </td>
                      <td data-label={t("admin.profile.terms.actions")}>
                        <div className="admin-organization-users-actions">
                          <a
                            aria-label={t("admin.detail.field.viewUrl")}
                            className="admin-organization-users-icon-action"
                            href={domain.url}
                            rel="noreferrer"
                            target="_blank"
                            title={t("admin.detail.field.viewUrl")}
                          >
                            <FaExternalLinkAlt aria-hidden="true" />
                          </a>
                          <AdminActionButton
                            ariaLabel={t("admin.copy.copy")}
                            className="admin-organization-users-icon-action"
                            onClick={() => {
                              void copyToClipboard(domain.url);
                            }}
                            t={t}
                            title={t("admin.copy.copy")}
                          >
                            <FaRegCopy aria-hidden="true" />
                          </AdminActionButton>
                          <AdminActionButton
                            ariaLabel={t("admin.detail.field.qrCode")}
                            className="admin-organization-users-icon-action"
                            disabled={!domain.selectable}
                            onClick={() => setQrPopupDomain(domain)}
                            t={t}
                            title={t("admin.detail.field.qrCode")}
                          >
                            <FaQrcode aria-hidden="true" />
                          </AdminActionButton>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="admin-detail-empty">{t("admin.sign.urls.empty")}</p>
          )}
        </section>
      </AdminDetailSectionLayout>
      <AppPopup
        actions={(
          <>
            <button
              className="admin-popup-action"
              disabled={isBusy}
              onClick={() => setPopup(null)}
              type="button"
            >
              {t("admin.popup.cancel")}
            </button>
            <button
              className="admin-popup-action primary"
              disabled={isBusy}
              onClick={() => void handlePopupConfirm()}
              type="button"
            >
              {t("admin.popup.confirm")}
            </button>
          </>
        )}
        isOpen={popup !== null}
        onClose={() => setPopup(null)}
        title={popupTitle}
      >
        <p>{popupBody}</p>
      </AppPopup>
      <AppPopup
        actions={(
          <>
            <button
              className="admin-popup-action"
              onClick={() => setQrPopupDomain(null)}
              type="button"
            >
              {t("admin.popup.close")}
            </button>
            <button
              className="admin-popup-action primary"
              onClick={() => {
                void qrCodeRef.current?.exportSvg();
              }}
              type="button"
            >
              {t("admin.export.svg")}
            </button>
          </>
        )}
        isOpen={qrPopupDomain !== null}
        onClose={() => setQrPopupDomain(null)}
        title={t("admin.detail.field.qrCode")}
      >
        {qrPopupDomain ? (
          <>
            <ViewUrlQrCode
              fileName={buildQrFileNameForDomain(qrPopupDomain)}
              ref={qrCodeRef}
              url={qrPopupDomain.url}
            />
            <p className="admin-sign-qr-link-wrap">
              <a
                className="admin-sign-qr-link"
                href={qrPopupDomain.url}
                rel="noreferrer"
                target="_blank"
              >
                {qrPopupDomain.url}
              </a>
            </p>
          </>
        ) : null}
      </AppPopup>
    </>
  );
};
